import React from 'react';
import LegalPage from './LegalPage';

const PrivacyPage: React.FC = () => {
  return (
    <LegalPage
      title="Privacy Policy"
      subtitle="How GenLend collects, uses, and protects your personal information."
      lastUpdated="To be finalised"
    >
      <h2>1. Introduction</h2>
      <p>
        GenLend is committed to protecting your personal information and respecting your privacy.
        This Privacy Policy describes how we collect, use, store, and share your information when
        you use our platform, in compliance with the Protection of Personal Information Act, 2013
        (POPIA).
      </p>

      <h2>2. Who we are</h2>
      <p>
        GenLend is a short-term credit provider for South African tertiary students. Full company
        details and our Information Officer's contact information will be displayed here once
        finalised.
      </p>

      <h2>3. What information we collect</h2>
      <p>To provide our services we may collect the following categories of information:</p>
      <ul>
        <li><strong>Identity information:</strong> first name, last name, South African ID number, date of birth.</li>
        <li><strong>Contact information:</strong> mobile number, email address.</li>
        <li><strong>Educational information:</strong> institution name, student number, NSFAS status.</li>
        <li><strong>Banking information:</strong> account holder name, bank name, account number, branch code, account type.</li>
        <li><strong>Verification documents:</strong> NSFAS statement, bank statement, student card, and similar uploads.</li>
        <li><strong>Usage information:</strong> log data such as IP address, device type, browser, and pages visited.</li>
        <li><strong>Payment information:</strong> records of loan repayments and payment method (e.g. PayFast).</li>
      </ul>

      <h2>4. How we use your information</h2>
      <p>We use your information for the following purposes:</p>
      <ul>
        <li>To assess and process your loan application, including affordability and verification checks.</li>
        <li>To disburse approved loans and collect repayments.</li>
        <li>To send transactional notifications (e.g. application status, payment confirmations) by SMS, email, or in-app.</li>
        <li>To comply with our legal obligations under the National Credit Act, FICA, and other applicable laws.</li>
        <li>To prevent fraud, secure our platform, and protect our legitimate interests.</li>
        <li>To improve our products and customer experience.</li>
      </ul>

      <h2>5. Legal basis</h2>
      <p>
        We process your information based on one or more of the following: your consent, the
        performance of a contract with you (your loan agreement), our legal obligations as a credit
        provider, and our legitimate interests in running the GenLend service responsibly.
      </p>

      <h2>6. Sharing your information</h2>
      <p>We share your information only when necessary, with parties such as:</p>
      <ul>
        <li><strong>Service providers</strong> who help us run the platform (e.g. hosting, document storage, SMS, email).</li>
        <li><strong>Payment partners</strong> such as PayFast for processing payments.</li>
        <li><strong>Credit bureaux</strong> where required for credit checks and reporting.</li>
        <li><strong>Regulators and authorities</strong> when required by law (e.g. NCR, SARS, courts).</li>
      </ul>
      <p>
        We do not sell your personal information to third parties.
      </p>

      <h2>7. International transfers</h2>
      <p>
        Some of our service providers may process or store data outside South Africa. Where this
        happens, we take reasonable steps to ensure your information continues to be protected to a
        standard at least equivalent to South African law.
      </p>

      <h2>8. How we protect your information</h2>
      <p>
        We use a combination of technical and organisational measures designed to protect your
        information. These include encrypted connections (HTTPS), encrypted password storage,
        role-based access for our team, audit logs, and reputable third-party providers for hosting,
        document storage, and communications.
      </p>

      <h2>9. How long we keep your information</h2>
      <p>
        We keep your information only as long as is necessary for the purposes set out in this
        policy and as required by law (for example, credit and tax record-keeping requirements).
        After that, your information will be deleted or anonymised.
      </p>

      <h2>10. Your rights under POPIA</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Request access to the personal information we hold about you.</li>
        <li>Request that we correct any inaccurate or incomplete information.</li>
        <li>Object to certain types of processing, including direct marketing.</li>
        <li>Withdraw any consent you previously gave (where processing is based on consent).</li>
        <li>Lodge a complaint with the Information Regulator.</li>
      </ul>
      <p>
        To exercise any of these rights, please contact us using the details below.
      </p>

      <h2>11. Cookies and similar technologies</h2>
      <p>
        Our website uses a limited number of cookies and similar technologies to keep you logged in,
        remember your preferences, and understand how the service is used. You can control cookies
        through your browser settings.
      </p>

      <h2>12. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be communicated
        to you via email, SMS, or in-app notice. The "Last updated" date at the top will always
        reflect the most recent version.
      </p>

      <div className="contact-card">
        <h2>Contact our Information Officer</h2>
        <p>
          If you have any questions about how we handle your personal information, or wish to
          exercise any of your POPIA rights, contact us on WhatsApp at{' '}
          <a href="https://wa.me/27722734008" target="_blank" rel="noopener noreferrer">072 273 4008</a>
          {' '}or email info@genlend.co.za.
        </p>
        <p>
          You may also contact the <strong>Information Regulator of South Africa</strong> at
          {' '}<a href="https://inforegulator.org.za" target="_blank" rel="noopener noreferrer">inforegulator.org.za</a>.
        </p>
      </div>
    </LegalPage>
  );
};

export default PrivacyPage;
